import {
	createUploadParam,
	requestApiGet,
	requestApiGetFile,
	requestApiPostData,
	requestApiPut,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { AttachmentFile } from "../entities/AttachmentFile";
import type { Space } from "../entities/Space";
import type { SpaceActivity } from "../entities/SpaceActivity";
import type { SpaceDiskUsage } from "../entities/SpaceDiskUsage";
import type { SpaceNotification } from "../entities/SpaceNotification";
import { assert } from "../utils/BacklogUtil";
import type { GetRecentUpdatesParams } from "./params/GetRecentUpdatesParams";
import type { PostAttachmentFileParams } from "./params/PostAttachmentFileParams";
import type { UpdateSpaceNotificationParams } from "./params/UpdateSpaceNotificationParams";

/**
 * Get Space スペース情報の取得
 *
 * Returns information about your space.
 */
export function getSpace(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Space>> {
	return requestApiGet({
		config,
		path: "space",
		params: undefined,
		context,
	});
}

/**
 * Get Recent Updates 最近の更新の取得
 *
 * Returns recent updates in your space.
 */
export function getRecentUpdates(
	config: BacklogConfig,
	params?: GetRecentUpdatesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SpaceActivity[]>> {
	return requestApiGet({
		config,
		path: "space/activities",
		params,
		context,
	});
}

/**
 * Get Space Logo スペースアイコン画像の取得
 *
 * Returns logo image of your space.
 */
export function getSpaceLogo(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	return requestApiGetFile({
		config,
		path: "space/image",
		params: undefined,
		context,
	});
}

/**
 * Get Space Notification スペースのお知らせの取得
 *
 * Returns space notification.
 */
export function getSpaceNotification(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SpaceNotification>> {
	return requestApiGet({
		config,
		path: "space/notification",
		params: undefined,
		context,
	});
}

/**
 * Update Space Notification スペースのお知らせの更新
 *
 * Updates space notification.
 */
export function updateSpaceNotification(
	config: BacklogConfig,
	params: UpdateSpaceNotificationParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SpaceNotification>> {
	assert(params);
	return requestApiPut({
		config,
		path: "space/notification",
		params,
		context,
	});
}

/**
 * Get Space Disk Usage スペースの容量使用状況の取得
 *
 * Returns information about space disk usage.
 */
export function getSpaceDiskUsage(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SpaceDiskUsage>> {
	return requestApiGet({
		config,
		path: "space/diskUsage",
		params: undefined,
		context,
	});
}

/**
 * Post Attachment File 添付ファイルの送信
 *
 * Posts an attachment file for issue or wiki. Returns id of the attachment file.
 *
 * The file will be deleted after it has been attached. If attachment fails, the file will be deleted an hour later.
 */
export function postAttachmentFile(
	config: BacklogConfig,
	params: PostAttachmentFileParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<AttachmentFile>> {
	assert(params);
	const data = new FormData();

	const { contents, filename } = createUploadParam({ params });

	data.append("file", contents, filename);

	return requestApiPostData({
		config,
		path: "space/attachment",
		params: data,
		context,
	});
}
